import { Theme } from '@heidi/schema/ml-scribe';
import type { ConnectUI, ConnectUIEvent } from '@nangohq/frontend';

import { getIntegrationsDataConfig } from '../../../document-inbox/data/config';
import { getNangoConnectBaseUrl, getNangoHost } from './config';

export type OpenNangoConnectResult = 'connected' | 'closed';

export interface OpenNangoConnectOptions {
  sessionToken: string;
  onEvent?: (event: ConnectUIEvent) => void;
  /** Failsafe so the connect button never stays disabled forever. */
  timeoutMs?: number;
}

const DEFAULT_CONNECT_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * An open Radix dialog (Settings) sets `pointer-events: none` on `document.body`
 * and re-enables them only on the dialog layer. Nango appends its Connect iframe
 * to `body`, so the iframe paints above Settings but clicks fall through to the
 * page underneath until the dialog closes. A descendant with `pointer-events: auto`
 * is a hit target again.
 */
function enableConnectIframePointerEvents(connectUi: ConnectUI) {
  if (!connectUi.iframe) return;
  connectUi.iframe.style.pointerEvents = 'auto';
}

/**
 * On desktop the shell denies every `window.open` popup by default, and the
 * Connect UI iframe opens the provider OAuth window with `window.open` from a
 * cross-origin frame the app's link handler cannot reach. Arm the shell's
 * popup gate for the lifetime of the Connect UI so that popup is allowed
 * (see `set_oauth_popup_allowed` in `src-tauri/src/ipc/window.rs`).
 * Failures never block the flow: on an older shell without the command it
 * degrades to the previous behaviour (the Connect UI shows its
 * popup-blocked error). They are still reported, because a real IPC failure
 * on a current shell produces the same popup-blocked symptom.
 */
async function setDesktopOauthPopupGate(allowed: boolean): Promise<void> {
  const gate = getIntegrationsDataConfig().oauthPopupGate;
  await gate?.setAllowed(allowed).catch(() => undefined);
}

/**
 * next-themes applies `newBrand` / `newBrandDark`, not a bare `dark`.
 * `dark` / `light` are fallbacks for the future migration to those names.
 */
function resolveNangoTheme(): 'dark' | 'light' | 'system' {
  if (typeof document === 'undefined') return 'system';
  const { classList } = document.documentElement;
  if (classList.contains(Theme.newBrandDark) || classList.contains('dark')) return 'dark';
  if (classList.contains(Theme.newBrand) || classList.contains('light')) return 'light';
  return 'system';
}

/**
 * Opens Nango Connect UI with a BE-issued session token.
 * Resolves when the user connects successfully or closes the modal.
 * Requires `NEXT_PUBLIC_NANGO_HOST` so we never silently fall back to US Cloud.
 *
 * Important: `baseURL` is the Connect UI (iframe src). `apiURL` is the API.
 * Never set `baseURL` to `api.nango.dev` — that host refuses framing.
 */
export async function openNangoConnectUI(
  options: OpenNangoConnectOptions
): Promise<OpenNangoConnectResult> {
  const host = getNangoHost();
  if (!host) {
    throw new Error(
      'NEXT_PUBLIC_NANGO_HOST is required (do not default to Nango US Cloud)'
    );
  }

  const Nango = (await import('@nangohq/frontend')).default;
  const nango = new Nango({ host });
  const timeoutMs = options.timeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS;
  const baseURL = getNangoConnectBaseUrl(host);
  const themeOverride = resolveNangoTheme();

  await setDesktopOauthPopupGate(true);

  return await new Promise<OpenNangoConnectResult>((resolve, reject) => {
    let settled = false;
    const settle = (result: OpenNangoConnectResult) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(result);
    };
    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      reject(error);
    };

    const timeoutId = window.setTimeout(() => {
      void setDesktopOauthPopupGate(false);
      fail(new Error('Nango connect UI timed out'));
    }, timeoutMs);
    // Armed after an `error` event: `fail()` clears the main timeout, so
    // without this a user who abandons the Connect UI after an error (no
    // later connect/close event) would leave the gate armed for the rest of
    // the session. connect/close cancel it and disarm on their own.
    let errorDisarmFailsafeId: number | undefined;

    const connectUi = nango.openConnectUI({
      sessionToken: options.sessionToken,
      apiURL: host,
      baseURL,
      themeOverride,
      onEvent: (event) => {
        options.onEvent?.(event);
        // The gate disarms when the Connect UI actually goes away
        // (connect/close/timeout), NOT on `error`: the iframe stays open
        // with a "Try again" that reopens the OAuth popup without
        // re-entering this function, so disarming on error would make every
        // retry fail popup-blocked. connect/close can arrive after the
        // promise settled on an error — the disarm here still runs then.
        if (event.type === 'connect') {
          window.clearTimeout(errorDisarmFailsafeId);
          void setDesktopOauthPopupGate(false);
          settle('connected');
        } else if (event.type === 'close') {
          window.clearTimeout(errorDisarmFailsafeId);
          void setDesktopOauthPopupGate(false);
          settle('closed');
        } else if (event.type === 'error') {
          window.clearTimeout(errorDisarmFailsafeId);
          errorDisarmFailsafeId = window.setTimeout(() => {
            void setDesktopOauthPopupGate(false);
          }, timeoutMs);
          fail(
            new Error(
              event.payload.errorMessage ||
                event.payload.errorType ||
                'Nango connect UI error'
            )
          );
        }
      },
    });
    enableConnectIframePointerEvents(connectUi);
  });
}
